/* eslint-disable @typescript-eslint/no-unused-vars */
import {
	type GridRenderCellParams,
	GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import React, {
	ReactNode,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	alarmCardMapper,
	CustomStatus,
	CustomTooltipWithLabel,
	downloadFile,
	formatRelativeTimeForTable,
	LinkButton,
	NoDataComponent,
	renderHeaderActionButtons,
	SidePopover,
	TableDashboard,
	TooltipTheme,
	useAppDispatch,
	useAppSelector,
} from '../../../globalUtils/globalExports';
import {
	exportToCsvForAcChargerList,
	fetchAcChargersData,
	getAcChargerReducer,
	getAcFilterReducer,
	getacNocDashboardReducer,
	getAcReferenceDataReducer,
} from '../../rmsReduxStore/acReduxExports';
import { ActionIcons, IconWithTooltip } from '../../../globalUtils/globalIcons';
import {
	fetchChargersData, fetchChargerVisibilityReferenceData,
	getChargerReducer,
	getReferenceDataReducer,
} from '../../../rmsReduxStore/reduxExports';
import { refreshAcChargerActiveCertificateStatus, setAcActiveCertificateRefreshChargerId, setAcChargerListTableProps } from '../../rmsReduxStore/acChargersRedux/acChargerCreators';
import './AcManageChargersDashboard.css';
import {
	getAcFilterSelectedValue,
	getCurrentAcFiltersValues,
	getCurrentScreenAppliedAcFiltersCount,
	setAcFilterData,
} from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import access from '../../../auth/service/AccessControl';
import {
	fetchAcAlarmSeverityReferenceData,
	fetchAcChargerLocationReferenceData,
	fetchAcChargerProductTypeReferenceData,
	fetchAcChargerStationReferenceData,
	fetchAcSoftwarVersionReferenceData,
} from '../../rmsReduxStore/acReferenceDataRedux/acReferenceDataCreator';
import AcTableDashboard from '../../../globalUtils/TableDashboard/AcTableDashboard';
import AcFilterLayout, { FilterProp } from '../../../globalUtils/FilterLayout/AcFilterLayout';
import {
	IMapFocusedData,
	MapTooltipData,
} from '../../../components/chargers/ManageChargersDashboard';
import { createBreadCrumbProps } from '../../../globalUtils/globalHooks';
import { Menu, ThemeProvider, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import AddAcBulkCharger from './AddCharger/AddAcBulkCharger';
import { Id, toast } from 'react-toastify';

const AcManageChargersDashboard = memo(function AcManageChargersDashboard() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [addChargerOption, setAddChargerOption] = useState(null);
	const [exportAnchorEl, setExportAnchorEl] = useState(null);
	const [exportingFile, setExportingFile] = useState(false);
	const [isSidePopOverOpen, setIsSidePopOverOpen] = useState(false);
	const { chargers, chargerCount, chargerListTableProps, activeCertificateRefreshChargerId } =
		useAppSelector(getAcChargerReducer);
    const layouts = ['list', 'map'];
	const [layoutView, setLayoutView] = useState<string>(location?.state?.layoutView === 'map' ? 'map' : 'list');
	const chargersRef = useRef(chargers);
	const chargerListTablePropsRef = useRef(chargerListTableProps);
	const {nocDashboardChargerIdsForRedirection} = useAppSelector(getacNocDashboardReducer)
	const params = new URLSearchParams(location?.search);
	const chargerIdsFromState = params?.get('redirectedFrom') === 'nocDashboard' ? nocDashboardChargerIdsForRedirection : null;
	useEffect(() => {
		chargerListTablePropsRef.current = chargerListTableProps;
	}, [chargerListTableProps]);

	useEffect(() => {
		chargersRef.current = chargers;
	}, [chargers]);
	const {
		chargerProductTypeReferenceData,
		softwareVersionReferenceData,
		chargingStationReferenceData,
		acChargerLocationReferenceData,
		alarmSeverityReferenceData
	} = useAppSelector(getAcReferenceDataReducer);

	const {chargerVisibilityReferenceData} = useAppSelector(getReferenceDataReducer);

	const {chargerStatusReferenceData} = useAppSelector(getReferenceDataReducer);
	const [mapSelectedChargerDataPoint, setMapSelectedChargerDataPoint] =
		useState<IMapFocusedData>({ latitude: 0, longitude: 0, id: '' });
	const [individualChargerMapView, setIndividualChargerMapView] = useState(
		location?.state?.individualChargerMapView || false,
	);
	const [headerIndividualChargerMapView, setHeaderIndividualChargerMapView] =
		useState('Charger');
	const [mapDataPoints, setMapDataPoints] = useState<
		{
			latitude: number;
			longitude: number;
			status: string;
			id: string;
			tooltipData: MapTooltipData;
		}[]
	>();
	const [defaultMapCenter, setDefaultMapCenter] = useState(true);
	const [filtersVisible, setFiltersVisible] = useState(false);

	const generateOcppBackendJsonURL = (serverIp: string | null, serverPortNumber: number | null, hsPath: string | null, ocppId: string | null): string => {
		const normalizePath = (path?: string | null) => path?.replace(/^\/+|\/+$/g, '') ?? '';
		if(!serverIp || !serverPortNumber || !hsPath || !ocppId) return '';
		return `ws://${serverIp}:${serverPortNumber}/${normalizePath(hsPath)}/${ocppId}`;
	}

	useEffect(() => {
		layoutCallback(
			1,
			chargerListTablePropsRef?.current?.pageSize,
			chargerListTablePropsRef?.current?.view,
			chargerListTablePropsRef?.current?.sortBy,
			chargerListTablePropsRef?.current?.order,
			null,
			'',
		);
	}, []);
	const headerActionBtns = renderHeaderActionButtons({
		actionBtns: [
			{
          id: 'add_new_btn',
          isAccessible: access?.chargers?.add(),
          headerActionBtnClick: (event): void => {

            setAddChargerOption(event.currentTarget);
          }
        },
        {
				id: 'add_filter_btn',
				isAccessible: access?.chargers?.view(),
				headerActionBtnClick: (): void => {
					setFiltersVisible(true);
				},
				badgeCount: getCurrentScreenAppliedAcFiltersCount('acchargers')
			},
			{
				id: 'export_csv_btn',
				isAccessible: access?.chargers?.export(),
				headerActionBtnClick: (event): void => {
					setExportAnchorEl(event.currentTarget);
				}
			},
		],
	});

	const handleClose = (): void => {
		setExportAnchorEl(null);
	};

	const fileDownloadSuccessToast = useCallback((): Id => {
		setExportingFile(false);
		return toast.success('File downloaded.');
	}, []);

	const fileDownloadFailureToast = useCallback((): Id => {
		setExportingFile(false);
		return toast.warn('Something went wrong. Please export again.');
	}, []);

	const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
		if (exportingFile) {
			return;
		}
		try {
			const response = await dispatch(exportToCsvForAcChargerList({
				fileType: fileType,
				filters: getCurrentAcFiltersValues('acchargers')
			}));
			if (response?.status === 202 || response?.status === 200) {
				downloadFile({ url: response?.data?.url });
				fileDownloadSuccessToast();
			} else {
				fileDownloadFailureToast();
				setExportAnchorEl(null);
			}
		} finally {
			setExportingFile(false);
			setExportAnchorEl(null);
		}
	}, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast]);

	const onIconClick = (rowData, mode) => {
		navigate('/ac/charger-detail', { state: { mode: mode, rowData: rowData } });
	};

	const refreshMapDataPoints = useCallback(
		async (chargerValue) => {
			try {
				const mapDataPoint =
					chargerValue !== null &&
					chargerValue?.length > 0 &&
					chargerValue?.map((charger) => {
						const chargerStatusIcon = charger.status === 'Offline' ? 'map__tooltip__offline__icon' : charger?.alarmStatus === 'Critical' && charger?.status === 'Online' ? 'map__tooltip__faulty__icon' : charger.status === 'Online' ? 'map__tooltip__online__icon' : '';
						return {
							latitude: parseFloat(charger?.latitude),
							longitude: parseFloat(charger?.longitude),
							status:
								charger?.alarmStatus === 'Critical' &&
								charger?.status === 'Online'
									? 'Faulty'
									: charger?.status,
							id: charger?.id,
							tooltipData: {
								id: charger?.id,
								headerText: charger.visibleId,
								headerOnClick: (): void => {
									navigate('/ac/charger?chargerId=' + charger.visibleId, {
										state: {
											layoutView: chargerValue?.length > 1 ? 'map' : 'list',
											chargerId: charger?.id,
											pageNumber:
												chargerListTablePropsRef?.current?.pageNumber || 1,
											chargerValue: chargerValue,
											individualChargerMapView: false,
											charger: charger,
										},
									});
								},
								data: [
									{
										icon: 'map__tooltip__customer__icon',
										text: `${charger.customerName}`,
									},
									{
										icon: 'map__tooltip__connector__icon',
										text: `${charger.connectorTypes}`,
									},
									{
										icon: chargerStatusIcon,
										text: `${charger?.alarmStatus === 'Critical' && charger?.status === 'Online' ? 'Faulty' : charger?.status}`,
									},
								],
							},
						};
					});
				if (mapDataPoint) {
					const filteredDataPoints = mapDataPoint.filter(
						(i) =>
							i.latitude != null &&
							true &&
							!isNaN(i.latitude) &&
							i.longitude != null &&
							true &&
							!isNaN(i.longitude),
					);
					setMapDataPoints(filteredDataPoints);
				} else {
					setMapDataPoints([]); // If there are no chargers coming from server.
				}
			} catch (error) {
				console.error('Error in refreshMapDataPoints:', error);
			}
		},
		[chargerListTablePropsRef?.current, setMapDataPoints],
	);

	const onMapIconClick = useCallback(
		async (event, data) => {
			const charger: ICharger = data;
			const mapData: IMapFocusedData = {
				latitude: Number(charger?.latitude) || 0,
				longitude: Number(charger?.longitude) || 0,
				id: charger?.id,
			};
			setMapSelectedChargerDataPoint(mapData);
			await refreshMapDataPoints([data]);
			setHeaderIndividualChargerMapView(
				charger?.customerName + ', ' + charger?.city,
			);
			setLayoutView('map');
			setIndividualChargerMapView(true);
			setDefaultMapCenter(false);
			history.pushState(null, '', '/ac/manage-chargers');
			const tableProps: IChargerListTableProps = cloneDeep(
				chargerListTablePropsRef.current,
			);
			tableProps.searchTerm = '';
			dispatch(setAcChargerListTableProps(tableProps));
		},
		[
			mapSelectedChargerDataPoint,
			defaultMapCenter,
			headerIndividualChargerMapView,
			chargerListTablePropsRef?.current,
		],
	);


	const handleLayoutToggle = useCallback(
		async (newLayoutVal, newIndividualChargerMapView?) => {
			if (newLayoutVal === 'map') {
				setDefaultMapCenter(true);
				setMapSelectedChargerDataPoint({ latitude: 0, longitude: 0, id: '' });
				await refreshMapDataPoints(chargersRef.current);
			}
			setLayoutView(newLayoutVal);
			if (
				newIndividualChargerMapView !== undefined &&
				individualChargerMapView != newIndividualChargerMapView
			)
				setIndividualChargerMapView((prevState) => !prevState); // when we click on Back button in Table Dashboard, we pass newIndividualChargerMapView as false
		},
		[chargersRef.current, setIndividualChargerMapView],
	);

	const layoutsConfiguration = useMemo(() => {
		return [
			{
				toolTipText: 'List View',
				onIconClick: (): void => {
					handleLayoutToggle('list');
				},
				layout: 'list',
				iconClassName: 'rms__table__dashboard-list__view__icon',
			},
			{
				toolTipText: 'Map View',
				onIconClick: (): void => {
					handleLayoutToggle('map');
				},
				layout: 'map',
				iconClassName: 'rms__table__dashboard-map__view__icon',
			},
		];
	}, [handleLayoutToggle]);
	

	const actionIconsList = useMemo(() => {
		return [
			{
				icon: 'viewIcon',
				isAccessible: access?.chargers?.view(),
				action: (_, rowData) => onIconClick(rowData, 'View'),
			},
			{
				icon: 'sheildIcon',
				isAccessible: access?.chargers?.view(),
				action: onMapIconClick,
			},
			{
				icon: 'pencilIcon',
				isAccessible: access?.chargers?.edit(),
				action: (_, rowData) => onIconClick(rowData, 'Edit'),
			},
			// Needs to be deployed after uncommenting once we get approval.
			// {
			//   icon: 'deleteIcon',
			//   isAccessible:  access?.chargers?.edit(),
			//   action: (_, rowData) => deleteIconClick(rowData?.id)
			// },
			// {icon: 'pencilWhiteIcon',
			// isAccessible:  access?.chargers?.edit()
			// },
		];
	}, [onMapIconClick, chargerListTablePropsRef?.current]);

	// The in-progress refresh is kept in redux (which is persisted), so clear any leftover value when the screen opens or closes
	useEffect(() => {
		dispatch(setAcActiveCertificateRefreshChargerId(null));
		return () => { dispatch(setAcActiveCertificateRefreshChargerId(null)); };
	}, []);

	const handleRefreshChargerActiveCertStatus = useCallback(async (chargerId: string) => {
		if (!chargerId || activeCertificateRefreshChargerId) return;

		const response = await dispatch(refreshAcChargerActiveCertificateStatus(chargerId));
		if (response?.success) {
			toast.success(response?.message ?? 'Active certificate status refreshed.');
		} else {
			toast.error(response?.message ?? 'Unable to refresh active certificate status. Please try again.');
		}
	}, [dispatch, activeCertificateRefreshChargerId]);

	const chargerColumns = useMemo(() => {
		const columns = [
			{
				field: 'alarmStatus',
				headerName: '',
				flex: 1,
				headerAlign: 'center',
				align: 'center',
				sortable: true,
				pinnable: false,
				minWidth: 20,
				maxWidth: 30,
				disableColumnMenu: true,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					const severityIcon =
						rowData?.alarmStatus === 'No Alarm'
							? 'TOTAL'
							: rowData?.alarmStatus?.toUpperCase();
					const tooltipText =
						rowData?.alarmStatus === 'No Alarm'
							? 'No Alarm'
							: alarmCardMapper[severityIcon]?.title;
					return (
						<div className='charger__connector__type__cell'>
							<IconWithTooltip
								iconClassName={
									rowData?.alarmStatus === 'No Alarm'
										? 'no_alarm_icon'
										: alarmCardMapper[severityIcon].alarmSummaryIcon
								}
								toolTipText={tooltipText}
							/>
						</div>
					);
				},
			},
			{
				field: 'visibleId',
				flex: 1,
				maxWidth: 500,
				headerName: 'Charger',
				minWidth: 200,
				hideable: false,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return rowData?.productType ? (
						<div className='individual__charger__wrap'>
							<LinkButton
								linkBtnId={rowData?.visibleId}
								link={'/ac/charger?chargerId=' + rowData.visibleId}
								btnClassName='individual__charger__text'
								btnText={rowData?.visibleId}
								linkBtnState={{ chargerId: rowData?.id, charger: rowData }}
							/>
						</div>
					) : (
						<div>{rowData?.visibleId}</div>
					);
				},
			},
			{
				field: 'ocppId',
				flex: 1,
				maxWidth: 500,
				headerName: 'OCPP ID',
				minWidth: 150,
				hideable: false,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.ocppId}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'chargingStation',
				headerName: 'Charging Station/Dealer',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 150,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.chargingStation}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'customer',
				headerName: 'Customer',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 120,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.customer}
							labelId={rowData?.id}
						/>
					);
				},
			},

			{
				field: 'status',
				headerName: 'Status',
				minWidth: 90,
				flex: 1,
				editable: false,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					return (
						<CustomStatus
							statusText={params?.row?.status}
							statusIconClassName={
								params?.row?.status === 'Online'
									? 'status__online__charger__icon'
									: 'status__offline__charger__icon'
							}
							backgroundColor={
								params?.row?.status === 'Online'
									? 'var(--green-color)'
									: 'var(--grey-color)'
							}
						/>
					);
				},
			},
			{
				field: 'lastPingTime',
				headerName: 'Last Ping Time',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): ReactNode => {
					const rowData = params?.row;
					return rowData?.lastPingTime
						? formatRelativeTimeForTable(new Date(rowData?.lastPingTime))
						: '-';
				},
			},
			{
				field: 'productType',
				flex: 1,
				maxWidth: 500,
				headerName: 'Product Type',
				minWidth: 150,
				hideable: false,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.productType}
							labelId={rowData?.id}
						/>
					);
				},
			},
			// {
			// 	field: 'warranty',
			// 	flex: 1,
			// 	maxWidth: 500,
			// 	headerName: 'Warranty',
			// 	minWidth: 150,
			// 	hideable: false,
			// 	pinnable: false,
			// },

			{
				field: 'softwareVersion',
				headerName: 'Software Version',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.softwareVersion}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'softwareVersionLastUpdate',
				headerName: 'Last SW Version Update time',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): ReactNode => {
					const rowData = params?.row;
					return rowData?.softwareVersionLastUpdate
						? formatRelativeTimeForTable(
								new Date(rowData?.softwareVersionLastUpdate),
							)
						: '-';
				},
			},
			// {
			// 	field: 'ccuVersion',
			// 	headerName: 'CCU SW Version',
			// 	flex: 1,
			// 	sortable: true,
			// 	pinnable: false,
			// 	minWidth: 140,
			// 	renderCell: (
			// 		params: GridRenderCellParams<GridValidRowModel>,
			// 	): ReactNode => {
			// 		const rowData = params?.row;
			// 		return rowData?.ccuVersion ? rowData?.ccuVersion : '-';
			// 	},
			// },
			{
				field: 'bootLoaderVersion',
				headerName: 'Boot Loader Version',
				flex: 1,
				sortable: true,
				pinnable: false,
				align: 'center',
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.bootLoaderVersion}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'em1SwVersion',
				headerName: 'EM1 SW Version',
				flex: 1,
				sortable: true,
				pinnable: false,
				align: 'center',
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.em1SwVersion}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'em2SwVersion',
				headerName: 'EM2 SW Version',
				flex: 1,
				sortable: true,
				pinnable: false,
				align: 'center',
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.em2SwVersion}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'modemSignalIntensity',
				headerName: 'Modem Signal Intensity',
				flex: 1,
				sortable: true,
				pinnable: false,
				align: 'center',
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={String(rowData?.modemSignalIntensity || '-')}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'lastSetAlarmName',
				headerName: 'Last Alarm',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					const lastSetAlarmName = rowData?.lastSetAlarmName;
					const labelId = params?.row?.id;
					return (
						<CustomTooltipWithLabel
							label={lastSetAlarmName}
							labelId={labelId}
						/>
					);
				},
			},
			{
				field: 'lastAlarmSetTime',
				headerName: 'Last Alarm Reported Time',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): ReactNode => {
					const rowData = params?.row;
					return rowData?.lastAlarmSetTime
						? formatRelativeTimeForTable(new Date(rowData?.lastAlarmSetTime))
						: '-';
				},
			},
			{
				field: 'ocppBackendJSONURL',
				headerName: 'OCPP Backend JSON URL',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 160,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					const ocppBackendJSONURL = generateOcppBackendJsonURL(rowData?.serverIp, rowData?.serverPortNumber, rowData?.hsPath, rowData?.ocppId);
					const labelId = params?.row?.id;
					return (
						<CustomTooltipWithLabel
							label={ocppBackendJSONURL}
							labelId={labelId}
						/>
					);
				},
			},
			{
				field: 'connectorTypes',
				headerName: 'Connectors',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 130,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					const connectorTypes = rowData?.connectorTypes;
					const labelId = params?.row?.id;
					return (
						<CustomTooltipWithLabel label={connectorTypes} labelId={labelId} />
					);
				},
			},
			{
				field: 'activeCertificate',
				headerName: 'Active Certificate',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 120,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					const isRefreshing = rowData?.id === activeCertificateRefreshChargerId;
					const isDisabled = !!activeCertificateRefreshChargerId && !isRefreshing;
					return (
						<div className='ac_charger_active_certificate_status'>
							<CustomTooltipWithLabel
								label={rowData?.activeCertificate}
								labelId={rowData?.id}
							/>
							{rowData?.activeCertificate !== 'Charger Specific' &&
								<ThemeProvider theme={TooltipTheme}>
									<Tooltip arrow title={'Refresh the certificate status for the charger.'}>
										<div
											className={`rms__icon rms__dashboard__refresh__icon${isRefreshing ? ' animation' : ''}${isDisabled ? ' disabled' : ''}`}
											aria-disabled={isDisabled}
											onClick={(e) => {
												e.stopPropagation();
												if (isDisabled || isRefreshing) return;
												handleRefreshChargerActiveCertStatus(rowData?.id);
											}}
										></div>
									</Tooltip>
								</ThemeProvider>
							}
						</div>
					);
				},
			},
			{
				field: 'model',
				headerName: 'Model',
				minWidth: 130,
				editable: false,
				pinnable: false,
				flex: 1,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.model}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'city',
				headerName: 'City',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 120,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.city}
							labelId={rowData?.id}
						/>
					);
				},
			},

			{
				field: 'visibility',
				headerName: 'Visibility',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 100,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.visibility}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'lastUseChargingTime',
				headerName: 'Last Use Charging Time',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): ReactNode => {
					const rowData = params?.row;
					return rowData?.lastUseChargingTime
						? formatRelativeTimeForTable(new Date(rowData?.lastUseChargingTime))
						: '-';
				},
			},
			{
				field: 'lastWeekUptime',
				headerName: 'Last Week Uptime',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 100,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.lastWeekUptime}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'chargerState',
				headerName: 'Availability',
				flex: 1,
				sortable: true,
				pinnable: false,
				minWidth: 140,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={
								rowData?.chargerState === 'AVAILABLE'
									? 'Available'
									: 'Unavailable'
							}
							labelId={'charger__dashboard__availability'}
						/>
					);
				},
			},
			{
				field: 'remarks',
				headerName: 'Notes',
				minWidth: 100,
				flex: 1,
				sortable: false,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							labelId='charger__dashboard-notes'
							label={rowData?.remarks}
						/>
					);
				},
			},
			{
				field: 'dealerCode',
				headerName: 'Dealer Code',
				minWidth: 100,
				flex: 1,
				sortable: true,
				pinnable: false,
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel>,
				): JSX.Element => {
					const rowData = params?.row;
					return (
						<CustomTooltipWithLabel
							label={rowData?.dealerCode}
							labelId={rowData?.id}
						/>
					);
				},
			},
			{
				field: 'action',
				headerName: 'Actions',
				flex: 1,
				sortable: false,
				pinnable: false,
				minWidth: 120,
				headerAlign: 'center',
				align: 'left',
				renderCell: (
					params: GridRenderCellParams<GridValidRowModel, Date>,
				): JSX.Element => {
					const rowData = params?.row;
					const allowedActions =
						rowData?.isDeletable === 'true'
							? actionIconsList?.filter(
									(icon) => icon?.icon !== 'pencilWhiteIcon',
								)
							: actionIconsList?.filter((icon) => icon?.icon !== 'deleteIcon');
					return (
						<div className='charging__station__action__icons__wrap'>
							<ActionIcons actionIconsList={allowedActions} data={rowData} />
						</div>
					);
				},
			},

			// {
			// 	field: 'efficiency',
			// 	flex: 1,
			// 	maxWidth: 500,
			// 	headerName: 'Efficiency',
			// 	minWidth: 150,
			// 	hideable: false,
			// 	pinnable: false,
			// },
			// {
			// 	field: 'reactiveEnergyConsumption',
			// 	flex: 1,
			// 	maxWidth: 500,
			// 	headerName: 'Reactive Energy Consumption',
			// 	minWidth: 150,
			// 	hideable: false,
			// 	pinnable: false,
			// },
		];
		return columns;
	}, [activeCertificateRefreshChargerId, handleRefreshChargerActiveCertStatus]);

	const getTableRows = useCallback((): ICharger[] => {
		return chargers !== null && chargers?.length > 0 ? chargers : [];
	}, [chargers]);

	const mobileViewConfiguration = useMemo(() => {
		return {
			headerDataConfig: {
				headerLeftDataConfig: ['visibleId', 'connectorTypes'],
				headerRightDataConfig: {
					actionIconsComponent: false,
					statusIconComponent: true,
				},
			},
			statusIconKey: 'status',
			statusIconMapper: {
				Online: 'status__online__charger__icon',
				Offline: 'status__offline__charger__icon',
			},
			statusIconBackgroundColorMapper: {
				Online: 'var(--green-color)',
				Offline: 'var(--grey-color)',
			},
			contentDataConfig: [[]],
		};
	}, []);

	const layoutCallback = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			view: string,
			field: string,
			order: string,
			filterData,
			searchTerm,
		) => {
			const tableProps: IChargerListTableProps = cloneDeep(
				chargerListTablePropsRef.current,
			);
			const filters = getCurrentAcFiltersValues('acchargers');
			await dispatch(
				fetchAcChargersData({
					view: view ?? layoutView,
					sortBy: field || chargerListTablePropsRef?.current?.sortBy,
					order: order || chargerListTablePropsRef?.current?.order,
					pageSize,
					pageNumber,
					filters: filters,
					searchTerm:
						searchTerm != null && searchTerm != undefined
							? searchTerm
							: chargerListTablePropsRef?.current?.searchTerm,
					chargerIds: chargerIdsFromState,
				}),
			);
			if (tableProps) {
				tableProps.view = view || layoutView;
				tableProps.sortBy = field || chargerListTablePropsRef?.current?.sortBy;
				tableProps.order = order || chargerListTablePropsRef?.current?.order;
				tableProps.pageNumber = pageNumber;
				tableProps.pageSize = pageSize;
				if (searchTerm != null && searchTerm != undefined) {
					tableProps.searchTerm = searchTerm;
				}
			}
			await dispatch(setAcChargerListTableProps(tableProps));
		},
		[layoutView, chargerListTablePropsRef?.current, dispatch],
	);
	const onChangeOfSearchText = useCallback(
		async (val) => {
			if (chargerListTablePropsRef?.current) {
				await layoutCallback(
					1,
					chargerListTablePropsRef?.current?.pageSize,
					chargerListTablePropsRef?.current?.view,
					chargerListTablePropsRef?.current?.sortBy,
					chargerListTablePropsRef?.current?.order,
					null,
					val,
				);
			}
		},
		[layoutView, chargerListTablePropsRef?.current, chargersRef?.current],
	);
	const searchBoxConfiguration = useMemo(() => {
		return {
			searchFieldId: 'manage-chargers-search-box',
			searchFieldName: 'manage-chargers-search-box',
			handleSearch: onChangeOfSearchText,
		};
	}, [onChangeOfSearchText, layoutView, chargerListTablePropsRef?.current]);

	const handleFilterDataChange = useCallback(
		async (val, filterKey, isGlobal) => {
			const res = await setAcFilterData(
				val,
				filterKey,
				'acchargers',
				'SET',
				isGlobal ? isGlobal : false,
			)(dispatch);
			if (res?.message === 'Action dispatched successfully') {
				if (chargerListTablePropsRef?.current) {
					await layoutCallback(
						1,
						chargerListTablePropsRef?.current?.pageSize,
						chargerListTablePropsRef?.current?.view,
						chargerListTablePropsRef?.current?.sortBy,
						chargerListTablePropsRef?.current?.order,
						res?.filters,
						chargerListTablePropsRef?.current?.searchTerm,
					);
				}
			}
		},
		[
			chargerListTablePropsRef?.current,
			dispatch,
			chargersRef?.current,
			layoutView,
		],
	);
	const chargerFilterProps: FilterProp[] = useMemo(() => {
		const chargerFilters: FilterProp[] = [
			// {
			// 	filterLabel: 'Connector Types',
			// 	filterType: 'dropdown',
			// 	filterId: 'chargerType',
			// 	filterDropdownProps: {
			// 		selectValue: getAcFilterSelectedValue(
			// 			'chargerType',
			// 			'acchargers',
			// 			false,
			// 		),
			// 		selectOptions: [],
			// 		handleSelectOptionChange: (val): void => {
			// 			handleFilterDataChange(val, 'chargerType', false);
			// 		},
			// 		selectDropDownId: 'charger-charger-type-filter-dropdown',
			// 		isMultiSelect: false,
			// 		renderToBody: true,
			// 	},
			// },
			{
				filterLabel: 'Product Type',
				filterType: 'dropdown',
				filterId: 'productType',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'productType',
						'acchargers',
						false,
					),
					selectOptions: chargerProductTypeReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'productType', false);
					},
					selectDropDownId: 'charger-charger-type-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},
			// {
			// 	filterLabel: 'Active Certificate',
			// 	filterType: 'dropdown',
			// 	filterId: 'activeCertificate',
			// 	filterDropdownProps: {
			// 		selectValue: getAcFilterSelectedValue(
			// 			'activeCertificate',
			// 			'acchargers',
			// 			false,
			// 		),
			// 		selectOptions: [],
			// 		handleSelectOptionChange: (val): void => {
			// 			handleFilterDataChange(val, 'activeCertificate', false);
			// 		},
			// 		selectDropDownId: 'charger-charger-type-filter-dropdown',
			// 		isMultiSelect: true,
			// 		renderToBody: true,
			// 	},
			// },
			{
				filterLabel: 'Charging Stations',
				filterType: 'dropdown',
				filterId: 'chargingStationId',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'chargingStationId',
						'acchargers',
						false,
					),
					selectOptions: chargingStationReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'chargingStationId', false);
					},
					selectDropDownId: 'charger-chargingStationId-type-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},
			{
				filterLabel: 'Location',
				filterType: 'dropdown',
				filterId: 'Location',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'location',
						'acchargers',
						false,
					),
					selectOptions: acChargerLocationReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'location', false);
					},
					selectDropDownId: 'charger-location-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},
			{
				filterLabel: 'Status',
				filterType: 'dropdown',
				filterId: 'Status',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'chargerStatus',
						'acchargers',
						false,
					),
					selectOptions: chargerStatusReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'chargerStatus', false);
					},
					selectDropDownId: 'charger-status-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},
			{
				filterLabel: 'Alarm Severity',
				filterType: 'dropdown',
				filterId: 'alarmStatus',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'alarmStatus',
						'acchargers',
						false,
					),
					selectOptions: alarmSeverityReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'alarmStatus', false);
					},
					selectDropDownId: 'charger-severity-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},
			{
				filterLabel: 'Visibility',
				filterType: 'dropdown',
				filterId: 'visibility',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'visibility',
						'acchargers',
						false,
					),
					selectOptions: chargerVisibilityReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'visibility', false);
					},
					selectDropDownId: 'charger-software-version-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
				},
			},

			{
				filterLabel: 'Software Version',
				filterType: 'dropdown',
				filterId: 'softwareVersion',
				filterDropdownProps: {
					selectValue: getAcFilterSelectedValue(
						'softwareVersion',
						'acchargers',
						false,
					),
					selectOptions: softwareVersionReferenceData ?? [],
					handleSelectOptionChange: (val): void => {
						handleFilterDataChange(val, 'softwareVersion', false);
					},
					selectDropDownId: 'charger-software-version-filter-dropdown',
					isMultiSelect: true,
					renderToBody: true,
					menuPlacement: 'top'
				},
			},
		];

		return chargerFilters;
	}, [chargerListTablePropsRef?.current, chargersRef?.current, layoutView]);
	const handleFiltersClosePopOver = (): void => {
		setFiltersVisible(false);
	};

	useEffect(() => {
		dispatch(fetchAcChargerProductTypeReferenceData());
		dispatch(fetchAcChargerLocationReferenceData());
		dispatch(fetchAcSoftwarVersionReferenceData());
		dispatch(fetchAcChargerStationReferenceData());
		dispatch(fetchAcAlarmSeverityReferenceData());
		dispatch(fetchChargerVisibilityReferenceData());
	}, []);

	const breadCrumbs = createBreadCrumbProps({
		breadCrumbProps: [
			{
				objectType: 'link',
				id: 'chargers',
				linkBtnState: location?.state,
				handleOnClick: () => {
					setLayoutView('list');
					setIndividualChargerMapView(false);
				},
			},
			{
				objectType: 'text',
				id: 'text',
				text: headerIndividualChargerMapView,
			},
		],
	});

	const getMapData = useCallback(() => {
		if (mapDataPoints) {
			return mapDataPoints ?? [];
		}
	}, [mapDataPoints]);

	const handleCloseAddCharger = (): void => {
		setAddChargerOption(null);
	};

	const handleClosePopOver = (): void => {
		setIsSidePopOverOpen(false);
		setAddChargerOption(null);
	};
	const handlePopOverClose = (refresh: boolean): void => {
		setIsSidePopOverOpen(false);
		if (refresh) {
			layoutCallback(
				1,
				chargerListTablePropsRef?.current?.pageSize,
				chargerListTablePropsRef?.current?.view,
				chargerListTablePropsRef?.current?.sortBy,
				chargerListTablePropsRef?.current?.order,
				null,
				chargerListTablePropsRef?.current?.searchTerm,
			);
		}
	};
	return (
		<>
			<AcTableDashboard
				layoutsEnabled={true}
				tableId='manage-chargers-table'
				tableClassName='manage__chargers__table'
				header={'AC Chargers'}
				searchBoxIncluded={true}
				searchBoxConfiguration={searchBoxConfiguration}
				gridColumns={chargerColumns}
				tableRows={getTableRows()}
				mobileViewConfiguration={mobileViewConfiguration}
				layouts={layouts}
				layoutView={layoutView}
				headerActionBtns={headerActionBtns}
				listLayoutCallBack={layoutCallback}
				mapDataPoints={getMapData()}
				totalCount={chargerCount}
				showSkeleton={!chargers}
				totalCountText={'chargers'}
				showLoader={false}
				mapFocusedDataPoint={mapSelectedChargerDataPoint}
				usedefaultMapCenter={defaultMapCenter}
				customNoDataComponent={NoDataComponent}
				pageNumber={chargerListTableProps?.pageNumber}
				tablePageSize={chargerListTableProps?.pageSize}
				sortField={chargerListTableProps?.sortBy}
				sortOrder={chargerListTableProps?.order}
				layoutsConfiguration={layoutsConfiguration}
				individualChargerMapView={individualChargerMapView}
				breadCrumbs={breadCrumbs}
				showRefreshButton={false}
				leftPinnedColumns={['alarmStatus', 'visibleId']}
			/>
			<Menu
				id='export-option-dropdown'
				anchorEl={addChargerOption}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				keepMounted
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				className='export__option__dropdown_bulk'
				sx={{ top: '30px' }}
				open={Boolean(addChargerOption)}
				onClose={handleCloseAddCharger}
			>
				<MenuItem>
					<div
						className='export__btn-option'
						onClick={(e) => {
							e?.stopPropagation();
							navigate('/ac/add-charger', { state: { mode: 'Add' } });
						}}
					>
						Add Charger
					</div>
				</MenuItem>
				<MenuItem>
					<div
						className='export__btn-option'
						onClick={(e) => {
							e?.stopPropagation();
							setIsSidePopOverOpen(true);
							handleCloseAddCharger();
						}}
					>
						Bulk Upload
					</div>
				</MenuItem>
			</Menu>
			<Menu
				id='export-option-dropdown'
				anchorEl={exportAnchorEl}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				keepMounted
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				className='export__option__dropdown'
				sx={{ top: '30px' }}
				open={Boolean(exportAnchorEl)}
				onClose={handleClose}
			>
				<MenuItem>
					<div
						className='export__btn-option'
						onClick={(e) => {
							e?.stopPropagation();
							onExportBtnClick('csv');
						}}
					>
						To CSV
					</div>
				</MenuItem>
				<MenuItem>
					<div
						className='export__btn-option'
						onClick={(e) => {
							e?.stopPropagation();
							onExportBtnClick('xlsx');
						}}
					>
						To Excel
					</div>
				</MenuItem>
			</Menu>
			{isSidePopOverOpen && (
				<SidePopover
					id={'charger__bulk__upload__side__popover'}
					open={isSidePopOverOpen}
					containerClassName='charger__bulk__upload__side__popover'
					handleClose={handleClosePopOver}
					headerText={'Add Chargers To Exicom SPIN RMS'}
					// eslint-disable-next-line react/no-children-prop
					children={<AddAcBulkCharger handlePopOverClose={handlePopOverClose} />}
				/>
			)}
			{filtersVisible && (
				<SidePopover
					id={'manage__chargers__side__filters'}
					open={filtersVisible}
					containerClassName='manage__chargers__side__filters'
					handleClose={handleFiltersClosePopOver}
					headerText={' Add Filters'}
					// eslint-disable-next-line react/no-children-prop
					children={
						<AcFilterLayout
							requiredReferenceDataCallbacks={
								[
									'location',
									'status',
									'softwareVersion',
									'productType',
									'activeCertificate',
									'severity',
									'visibility',
									'chargingStationId',
									'chargerType',
									'ccuVersion',
								] /* ['location', 'status', 'softwareVersion', 'severity', 'chargerVisibleId'] */
							}
							filters={chargerFilterProps}
							handleFilterDataChange={handleFilterDataChange}
						/>
					}
				/>
			)}
		</>
	);
});

export default AcManageChargersDashboard;
